import React, { useCallback, useEffect, useRef, useState } from 'react';
import { COUNTRIES, POPULAR_ISO } from '../data/countries';
import '../styles/CountryCodePicker.css';

/**
 * CountryCodePicker — searchable dial-code selector with flag emoji.
 *
 * Props:
 *   value    {string}   Current dial code, e.g. "+61"
 *   onChange {function} Called with the new dial code string
 *   id       {string}   id for the trigger button (for label association)
 *   hasError {boolean}  Shows error border when true
 *   disabled {boolean}  Disables the trigger
 *   size     {string}   "md" (default) | "sm" — controls trigger height
 */
export default function CountryCodePicker({ value, onChange, id, hasError, disabled, size = 'md' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  // Find the currently selected country entry (first match by dial code)
  const selected = COUNTRIES.find((c) => c.dial === value) || COUNTRIES[0];

  // Split countries into popular + others for display
  const popular = COUNTRIES.filter((c) => POPULAR_ISO.includes(c.iso));
  const others = COUNTRIES.filter((c) => !POPULAR_ISO.includes(c.iso));

  // Flat filtered list when searching
  const query = search.trim().toLowerCase();
  const filtered = query
    ? COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.dial.includes(query) ||
          c.iso.toLowerCase().includes(query),
      )
    : null; // null = show sectioned list

  const openDropdown = useCallback(() => {
    setOpen(true);
    setSearch('');
    setActiveIndex(-1);
    setTimeout(() => searchRef.current?.focus(), 30);
  }, []);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setSearch('');
    setActiveIndex(-1);
    // Return focus to the trigger
    containerRef.current?.querySelector('.ccp-trigger')?.focus();
  }, []);

  const selectCountry = useCallback(
    (dial) => {
      onChange(dial);
      closeDropdown();
    },
    [onChange, closeDropdown],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) closeDropdown();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closeDropdown]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); closeDropdown(); }
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, [open, closeDropdown]);

  // Keyboard navigation inside the search input
  const handleSearchKeyDown = (e) => {
    const list = filtered || [...popular, ...others];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, list.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectCountry(list[activeIndex].dial);
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[role="option"]');
      items[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const renderOption = (c, globalIndex) => (
    <li
      key={c.iso}
      role="option"
      aria-selected={c.dial === value && c.iso === selected.iso}
      className={[
        'ccp-option',
        c.dial === value ? 'ccp-option--selected' : '',
        globalIndex === activeIndex ? 'ccp-option--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseDown={(e) => { e.preventDefault(); selectCountry(c.dial); }}
      onMouseEnter={() => setActiveIndex(globalIndex)}
    >
      <span className="ccp-option-flag" aria-hidden="true">{c.flag}</span>
      <span className="ccp-option-name">{c.name}</span>
      <span className="ccp-option-dial">{c.dial}</span>
      <span className="ccp-option-iso">{c.iso}</span>
    </li>
  );

  return (
    <div className={`ccp${size === 'sm' ? ' ccp--sm' : ''}`} ref={containerRef}>
      {/* ── Trigger button ── */}
      <button
        id={id}
        type="button"
        className={[
          'ccp-trigger',
          hasError ? 'ccp-trigger--error' : '',
          open ? 'ccp-trigger--open' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={open ? closeDropdown : openDropdown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country code: ${selected.name} ${selected.dial}`}
      >
        <span className="ccp-flag" aria-hidden="true">{selected.flag}</span>
        <span className="ccp-dial">{selected.dial}</span>
        <span
          className={`ccp-chevron${open ? ' ccp-chevron--up' : ''}`}
          style={{ fontSize: '12px', display: 'inline-block' }}
          aria-hidden="true"
        >
          ▼
        </span>
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div className="ccp-dropdown" role="listbox" aria-label="Select country code">
          {/* Search */}
          <div className="ccp-search-wrap">
            <span className="ccp-search-icon" style={{ fontSize: '14px' }} aria-hidden="true">🔍</span>
            <input
              ref={searchRef}
              className="ccp-search"
              type="text"
              placeholder="Country name or code"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setActiveIndex(-1); }}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search countries"
              autoComplete="off"
            />
            {search && (
              <button
                type="button"
                className="ccp-search-clear"
                onClick={() => { setSearch(''); setActiveIndex(-1); searchRef.current?.focus(); }}
                aria-label="Clear search"
              >×</button>
            )}
          </div>

          {/* List */}
          <ul className="ccp-list" ref={listRef} role="presentation">
            {filtered ? (
              // Search results — flat list
              filtered.length === 0 ? (
                <li className="ccp-empty">No country found for "{search}"</li>
              ) : (
                filtered.map((c, i) => renderOption(c, i))
              )
            ) : (
              // Sectioned list — popular first
              <>
                <li className="ccp-section-header" role="presentation">Popular</li>
                {popular.map((c, i) => renderOption(c, i))}
                <li className="ccp-section-header" role="presentation">Other countries</li>
                {others.map((c, i) => renderOption(c, popular.length + i))}
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
